import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const links = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Coding", id: "coding" },
  { label: "Skills", id: "skills" },
  { label: "Projects", id: "projects" },
  { label: "Achievements", id: "achievements" },
  { label: "Education", id: "education" },
  { label: "Experience", id: "experience" },
  { label: "Contact", id: "contact" },
];

function NavBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState("home");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Highlight whichever section is currently under the navbar
  useEffect(() => {
    const sections = links
      .map((link) => document.getElementById(link.id))
      .filter(Boolean);

    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-64px 0px -55% 0px", threshold: [0.1, 0.25, 0.5] }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  // Close the mobile menu on Escape
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isMenuOpen]);

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-slate-900/90 shadow-lg shadow-slate-900/20 backdrop-blur-md"
          : "bg-gradient-to-r from-blue-600 to-indigo-700"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <a
            href="#home"
            className="flex flex-shrink-0 items-center gap-2 text-lg font-bold tracking-wide text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent md:text-xl"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white font-extrabold text-indigo-700">
              M
            </span>
            Mayur Adlak
          </a>

          {/* Desktop links */}
          <div className="hidden lg:block">
            <ul className="flex items-baseline gap-1">
              {links.map((link) => {
                const isActive = activeId === link.id;
                return (
                  <li key={link.id}>
                    <a
                      href={`#${link.id}`}
                      aria-current={isActive ? "true" : undefined}
                      className={`relative rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                        isActive ? "text-white" : "text-blue-100/80 hover:text-white"
                      }`}
                    >
                      {link.label}
                      {isActive && (
                        <motion.span
                          layoutId="nav-active"
                          className="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full bg-white"
                          transition={{ type: "spring", stiffness: 400, damping: 32 }}
                        />
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMenuOpen((open) => !open)}
            type="button"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            className="inline-flex items-center justify-center rounded-md bg-white/15 p-2 text-white transition-colors duration-200 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 lg:hidden"
          >
            <span className="sr-only">{isMenuOpen ? "Close main menu" : "Open main menu"}</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence initial={false}>
        {isMenuOpen && (
          <motion.div
            id="mobile-menu"
            className="overflow-hidden lg:hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
          >
            <ul className="max-h-[70vh] space-y-1 overflow-y-auto border-t border-white/10 bg-slate-900/95 px-3 py-3 backdrop-blur-md">
              {links.map((link) => {
                const isActive = activeId === link.id;
                return (
                  <li key={link.id}>
                    <a
                      href={`#${link.id}`}
                      onClick={() => setIsMenuOpen(false)}
                      aria-current={isActive ? "true" : undefined}
                      className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-base font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                        isActive ? "bg-white/10 text-white" : "text-blue-100/80 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <span
                        className={`h-px transition-all duration-300 ${
                          isActive ? "w-5 bg-indigo-400" : "w-3 bg-white/30"
                        }`}
                      ></span>
                      {link.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export default NavBar;
